import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import AddCreditCard from './AddCreditCard';
import CreditCard, { CreditCardProps } from './CreditCard';
import CreditCardLoadingState from './CreditCardLoadingState';
import CreditCardPanel, { OnSaveCreditCard } from '../ProfilePanels/CreditCardPanel';
import { ExpiredSingleCardNudge } from '@/hooks/shared/useNudge/types';
import NudgeTooltip from '../Tooltip/NudgeTooltip';
import MicroformsCreditCardPanel from '../ProfilePanels/MicroformsCreditCardPanel';
import { useGetMicroFormCaptureContextMutation } from '@/redux/api/client/userPaymentMethods';
import useToast from '@/hooks/useToast';

type CreditCardGridProps = {
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  cards?: CreditCardProps[];
  onAddCardPress?: () => void;
  onSave?: OnSaveCreditCard;
  loading?: boolean;
  nudge?: ExpiredSingleCardNudge;
  isUsingMicroforms?: boolean;
};

const CreditCardGrid = ({
  isPanelOpen,
  setPanelOpen,
  cards,
  onAddCardPress,
  onSave,
  loading = false,
  nudge,
  isUsingMicroforms
}: CreditCardGridProps) => {
  const [captureContextToken, setCaptureContextToken] = useState<string | undefined>(undefined);
  const [microformsScriptLoaded, setMicroformsScriptLoaded] = useState(false);
  const { removeAllToasts } = useToast();

  const [getMicroFormCaptureContext, { data: microFormCaptureContextData }] =
    useGetMicroFormCaptureContextMutation();

  const microformRequest = useMemo(
    () => ({
      clientVersion: 'v2.0',
      targetOrigins: [`${window.location.origin}`],
      allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'DINERSCLUB']
    }),
    []
  );

  useEffect(() => {
    isPanelOpen ? getMicroFormCaptureContext(microformRequest) : removeAllToasts();
  }, [microformRequest, getMicroFormCaptureContext, isPanelOpen, removeAllToasts]);
  useEffect(() => {
    if (microFormCaptureContextData) {
      setCaptureContextToken(microFormCaptureContextData?.jwt ?? undefined);
    }
  }, [microFormCaptureContextData]);

  const loadMicroformScript = useCallback(async () => {
    if (!window.Flex) {
      const existingScript = document.querySelector(
        `script[src="${microFormCaptureContextData?.scriptSource}"]`
      );
      if (existingScript) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = microFormCaptureContextData?.scriptSource ?? '';
        script.type = 'text/javascript';
        script.async = true;
        script.integrity = microFormCaptureContextData?.integrityToken ?? '';
        script.crossOrigin = 'anonymous';

        setCaptureContextToken(microFormCaptureContextData?.jwt ?? undefined);

        script.onload = () => {
          setMicroformsScriptLoaded(true);
          resolve();
        };

        script.onerror = () => {
          console.error('Failed to load the Microform script.');
          reject(new Error('Failed to load the Microform script.'));
          setMicroformsScriptLoaded(false);
        };
        document.head.appendChild(script);
      });
    } else {
      setMicroformsScriptLoaded(true);
    }
  }, [
    microFormCaptureContextData?.integrityToken,
    microFormCaptureContextData?.jwt,
    microFormCaptureContextData?.scriptSource,
    setCaptureContextToken
  ]);

  useEffect(() => {
    microFormCaptureContextData && loadMicroformScript();
  }, [loadMicroformScript, microFormCaptureContextData, setCaptureContextToken]);

  const handleOnAddCardPress = () => {
    onAddCardPress?.();
    loadMicroformScript();
  };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {loading ? (
        Array.from({ length: (cards?.length || 0) + 1 }).map((_, index) => (
          <CreditCardLoadingState key={index} />
        ))
      ) : (
        <>
          {cards?.map(card => (
            <Fragment key={card.number}>
              {nudge?.cardId === card.id ? (
                <NudgeTooltip nudge={nudge}>
                  <CreditCard {...card} />
                </NudgeTooltip>
              ) : (
                <CreditCard {...card} />
              )}
            </Fragment>
          ))}
          {isUsingMicroforms ? ( //isUsingMicroforms can be deleted when Classic is retired
            <MicroformsCreditCardPanel
              isOpen={isPanelOpen}
              onOpenChange={setPanelOpen}
              onSave={onSave}
              triggerButton={<AddCreditCard onPress={handleOnAddCardPress} />}
              handleLoadMicroformScript={() => loadMicroformScript()}
              captureContextToken={captureContextToken}
              microformsScriptLoaded={microformsScriptLoaded}
            />
          ) : (
            <CreditCardPanel
              isOpen={isPanelOpen}
              onOpenChange={setPanelOpen}
              onSave={onSave}
              triggerButton={<AddCreditCard onPress={onAddCardPress} />}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CreditCardGrid;
